import React, { useState, useEffect } from "react";

import {
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragStartEvent,
    rectIntersection
} from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";

import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { TreeNodeRoute, TreeProps } from "./types/treeNode";
import { DropTarget, DropPosition, resolveDrop } from "./dropLogic";
import { TreeNode } from "./TreeNode";

/* ============================================================
   Helpers
   ============================================================ */

function cloneTree(nodes: TreeNodeRoute[]): TreeNodeRoute[] {
    return nodes.map(n => ({
        ...n,
        children: cloneTree(n.children ?? [])
    }));
}

// function buildInitialCollapsed(nodes: TreeNodeRoute[]): Set<string> {
//     if (!nodes.length) return new Set();

//     const openIds = new Set<string>();
//     const firstRoot = nodes[0];

//     const markOpenBranch = (node: TreeNodeRoute) => {
//         openIds.add(node.id);
//         node.children.forEach(markOpenBranch);
//     };

//     markOpenBranch(firstRoot);

//     const collapsedIds = new Set<string>();
//     const collect = (list: TreeNodeRoute[]) => {
//         list.forEach(n => {
//             if (n.children.length > 0 && !openIds.has(n.id)) {
//                 collapsedIds.add(n.id);
//             }
//             collect(n.children);
//         });
//     };

//     collect(nodes);

//     return collapsedIds;
// }

function updateNode(
    nodes: TreeNodeRoute[],
    id: string,
    updater: (n: TreeNodeRoute) => TreeNodeRoute
): TreeNodeRoute[] {
    return nodes.map(n => {
        if (n.id === id) {
            return updater(n);
        }
        return { ...n, children: updateNode(n.children, id, updater) };
    });
}

function cascadeChildren(node: TreeNodeRoute, included: boolean): TreeNodeRoute {
    return {
        ...node,
        included,
        children: node.children.map(c => cascadeChildren(c, included))
    };
}

function cascadeAncestors(nodes: TreeNodeRoute[], id: string): TreeNodeRoute[] {
    const cloned = cloneTree(nodes);

    const mark = (list: TreeNodeRoute[]): boolean => {
        let found = false;

        list.forEach(n => {
            const childFound = mark(n.children);

            if (n.id === id) found = true;

            // se eu ou filho estou incluso → manter incluído
            if (childFound || n.children.some(c => c.included)) {
                n.included = true;
            }
        });

        return found;
    };

    mark(cloned);

    return cloned;
}

function cascadeUncheckAncestors(nodes: TreeNodeRoute[], id: string): TreeNodeRoute[] {
    const cloned = cloneTree(nodes);

    // Retorna true se a subtree contém o nó que foi atualizado
    const apply = (list: TreeNodeRoute[]): boolean => {
        let subtreeContainsTarget = false;

        list.forEach(node => {
            const childContains = apply(node.children);

            // Se este nó é o alvo da ação
            if (node.id === id) {
                subtreeContainsTarget = true;
            }

            // Se a subtree contém o alvo, precisamos avaliar o estado deste nó também
            if (childContains || node.id === id) {
                const allChildrenUnchecked = node.children.every(c => !c.included);
                if (allChildrenUnchecked) {
                    node.included = false;
                }
            }

            // Mesmo se este nó não for o alvo, mas algum filho é, propagar retorno
            if (childContains) {
                subtreeContainsTarget = true;
            }
        });

        return subtreeContainsTarget;
    };

    apply(cloned);
    return cloned;
}



/* orderIndex após drag */
function updateOrderIndexes(nodes: TreeNodeRoute[]): TreeNodeRoute[] {
    return nodes.map((node, idx) => ({
        ...node,
        orderIndex: idx + 1,
        children: updateOrderIndexes(node.children)
    }));
}

export function InspectionRouteTree({
    initialData,
    onChange,
    options,
    renderNodeExtras,
    rowStyle
}: TreeProps) {
    const { t } = useTranslation();
    const tt = (key: string) => t(key, { ns: "translation" });

    const dragEnabled = options?.drag ?? true;
    const checkboxEnabled = options?.checkbox ?? true;
    const selectAllEnabled = options?.selectAll ?? true;

    /* árvore local */
    const [tree, setTree] = useState<TreeNodeRoute[]>(() => cloneTree(initialData));
    // const [collapsed, setCollapsed] = useState<Set<string>>(() => buildInitialCollapsed(initialData));
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

    /* recarrega quando initialData muda */
    useEffect(() => {
        setTree(cloneTree(initialData));
        // setCollapsed(buildInitialCollapsed(initialData));
    }, [initialData]);

    const updateLocalNode = (
        id: string,
        updater: (n: TreeNodeRoute) => TreeNodeRoute
    ) => {
        const newTree = updateNode(tree, id, updater);
        setTree(newTree);
        onChange?.(newTree);
    };


    const getAllIds = (nodes: TreeNodeRoute[]): string[] =>
        nodes.reduce(
            (acc, n) => [...acc, n.id, ...getAllIds(n.children)],
            [] as string[]
        );

    const allIds = getAllIds(tree);

    // Collapse ---------------------------------------------
    const onToggleCollapse = (id: string) => {
        setCollapsed(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    /* ============================================================
       Finders
       ============================================================ */

    const findNode = (list: TreeNodeRoute[], id: string): TreeNodeRoute | null => {
        for (const n of list) {
            if (n.id === id) return n;
            const c = findNode(n.children, id);
            if (c) return c;
        }
        return null;
    };

    const findParent = (
        list: TreeNodeRoute[],
        id: string,
        parent: TreeNodeRoute | null = null
    ): TreeNodeRoute | null => {
        for (const n of list) {
            if (n.id === id) return parent;
            const p = findParent(n.children, id, n);
            if (p) return p;
        }
        return null;
    };

    /* ============================================================
       Toggle included
       ============================================================ */

    const updateIncludedCascade = (id: string) => {
        const node = findNode(tree, id);
        if (!node) return;

        const newIncluded = !node.included;

        // Sempre aplica a marcação inicial
        let newTree = updateNode(tree, id, (n) =>
            cascadeChildren(n, newIncluded)
        );

        if (newIncluded) {
            // Selecionou, sobe marcando
            newTree = cascadeAncestors(newTree, id);
        } else {
            // Desmarcou, sobe desmarcando até o topo
            newTree = cascadeUncheckAncestors(newTree, id);
        }

        setTree(newTree);
        onChange?.(newTree);
    };



    /* ============================================================
       Select all
       ============================================================ */

    const allSelected = tree.every(n => n.included);

    const toggleSelectAll = () => {
        const include = !allSelected;

        const newTree = tree.map(t => cascadeChildren(t, include));
        setTree(newTree);

        onChange?.(newTree);
    };


    /* ============================================================
       Drag & Drop
       ============================================================ */

    const removeNode = (list: TreeNodeRoute[], id: string): TreeNodeRoute[] =>
        list
            .filter(n => n.id !== id)
            .map(n => ({ ...n, children: removeNode(n.children, id) }));

    const addAt = (
        list: TreeNodeRoute[],
        parent: TreeNodeRoute | null,
        node: TreeNodeRoute,
        index: number
    ): TreeNodeRoute[] => {
        if (!parent) {
            const arr = [...list];
            arr.splice(index, 0, node);
            return arr;
        }
        return list.map(n =>
            n.id === parent.id
                ? {
                    ...n,
                    children: [
                        ...n.children.slice(0, index),
                        node,
                        ...n.children.slice(index)
                    ]
                }
                : { ...n, children: addAt(n.children, parent, node, index) }
        );
    };

    const [activeId, setActiveId] = useState<string | null>(null);
    const [dropInfo, setDropInfo] = useState<DropTarget | null>(null);

    const handleDragStart = (e: DragStartEvent) => {
        if (!dragEnabled) return;
        setActiveId(String(e.active.id));
        setDropInfo(null);
    };

    const handleDragOver = (e: DragOverEvent) => {
        if (!dragEnabled) return;

        const { active, over } = e;
        if (!active || !over) return;

        const movingId = String(active.id);
        const targetId = String(over.id);

        if (movingId === targetId) {
            setDropInfo(null);
            return;
        }

        const moving = findNode(tree, movingId);
        const target = findNode(tree, targetId);
        if (!moving || !target) return setDropInfo(null);

        const movingParent = findParent(tree, movingId);
        const targetParent = findParent(tree, targetId);

        if ((movingParent?.id ?? null) !== (targetParent?.id ?? null)) {
            setDropInfo(null);
            return;
        }

        const siblings = movingParent ? movingParent.children : tree;

        const fromIndex = siblings.findIndex(n => n.id === movingId);
        const targetIndex = siblings.findIndex(n => n.id === targetId);

        const isBelow = fromIndex < targetIndex;
        const position: DropPosition = isBelow ? "below" : "above";

        setDropInfo({ node: target, position });
    };

    const handleDragEnd = (e: DragEndEvent) => {
        if (!dragEnabled) return;

        if (!dropInfo) {
            setDropInfo(null);
            setActiveId(null);
            return;
        }

        const movingId = String(e.active.id);
        const targetId = dropInfo.node.id;

        const moving = findNode(tree, movingId);
        const target = findNode(tree, targetId);
        if (!moving || !target) return;

        const movingParent = findParent(tree, movingId);
        const targetParent = findParent(tree, targetId);

        const drop = resolveDrop(
            moving,
            target,
            dropInfo.position,
            targetParent,
            tree,
            movingParent
        );

        if (!drop.allowed) return;

        let newTree = removeNode(tree, movingId);
        newTree = addAt(newTree, drop.newParent, moving, drop.insertIndex);

        newTree = updateOrderIndexes(newTree);

        setTree(newTree);
        onChange?.(newTree);

        setDropInfo(null);
        setActiveId(null);
    };


    /* ============================================================
       Render
       ============================================================ */

    return (
        <DndContext
            collisionDetection={rectIntersection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={() => {
                setActiveId(null);
                setDropInfo(null);
            }}
        >
            <SortableContext items={allIds}>
                <div style={{ padding: 20 }}>

                    {checkboxEnabled && selectAllEnabled && tree?.length > 0 && (
                        <Form.Check
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleSelectAll}
                            label={tt("pages.thermography.inspectionRouteTree.selectAll")}
                            style={{
                                marginBottom: 14,
                                fontWeight: 600,
                                fontSize: 14
                            }}
                        />
                    )}

                    {tree.map(node => (
                        <TreeNode
                            key={node.id}
                            node={node}
                            depth={0}
                            collapsed={collapsed}
                            activeId={activeId}
                            dropInfo={dropInfo}
                            onToggleIncluded={updateIncludedCascade}
                            onToggleCollapse={onToggleCollapse}
                            dragEnabled={dragEnabled}
                            checkboxEnabled={checkboxEnabled}
                            rowStyle={rowStyle}
                            renderNodeExtras={renderNodeExtras}
                            updateNode={updateLocalNode}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}
